import { SearchResult } from "@/types/searchResults";
import { supabase } from "@/integrations/supabase/client";

export async function searchWeb(
  query: string,
  isDeepSearch: boolean = true,
  useGPTOSS: boolean = true
): Promise<SearchResult> {
  try {
    const { data, error } = await supabase.functions.invoke('tavily-search', {
      body: { query, isDeepSearch, useGPTOSS }
    });

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (error) {
    console.error("Error searching with Tavily:", error);
    return {
      query,
      answer: "Sorry, I encountered an error while searching the web.",
      results: [],
      searchDepth: isDeepSearch ? "advanced" : "basic",
    };
  }
}

// New function for streaming web search with GPT-OSS
export async function searchWebWithStreaming(
  query: string,
  isDeepSearch: boolean = true,
  onMetadata: (metadata: any) => void,
  onContent: (content: string) => void,
  onComplete: (fullContent: string) => void,
  onError: (error: string) => void
): Promise<void> {
  const maxRetries = 3;
  let retryCount = 0;
  let lastError: Error | null = null;

  while (retryCount < maxRetries) {
    try {
      // Use the main Supabase project URL directly
      const supabaseUrl = 'https://arikhtozvkvxlraiuenh.supabase.co';
      const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU';

      const response = await fetch(
        `${supabaseUrl}/functions/v1/tavily-search`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({ query, isDeepSearch, useGPTOSS: true }),
        }
      );

      if (response.status === 429) {
        // Rate limited - wait and retry
        retryCount++;
        const waitTime = Math.min(1000 * Math.pow(2, retryCount), 8000); // Exponential backoff, max 8s
        console.warn(`Tavily rate limited (429). Retrying in ${waitTime}ms... (attempt ${retryCount}/${maxRetries})`);

        if (retryCount >= maxRetries) {
          onError('Search service is temporarily busy. Please wait a moment and try again.');
          return;
        }

        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Non-streaming JSON response from Edge Function
      const result = await response.json();
      onMetadata({
        type: 'metadata',
        query: result.query,
        searchDepth: result.searchDepth,
        results: result.results,
        images: result.images,
        gptEnhanced: result.gptEnhanced,
        answer: result.answer,
      });
      if (result.answer) {
        onComplete(result.answer);
      } else {
        onError('No answer returned from web search');
      }
      return; // Success, exit the retry loop
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      retryCount++;

      if (retryCount >= maxRetries) {
        console.error("Error in streaming web search:", lastError);
        onError(lastError.message || 'Unknown error occurred during search');
        return;
      }

      // Wait before retrying
      const waitTime = Math.min(1000 * Math.pow(2, retryCount), 8000);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
}

// Helper function to determine if dark mode is active
function isDarkMode(): boolean {
  return (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

// This function is now deprecated - use SearchResults React component instead
export function formatSearchResults(results: SearchResult): string {
  console.warn('formatSearchResults is deprecated. Use SearchResults React component instead.');

  if (!results.answer) {
    return "I couldn't find any relevant information.";
  }

  // Return a simple text response for backward compatibility
  return results.answer;
}
